import type { Brand } from "@shared/brand.types";
import type { CSSProperties } from "react";
import { hslToRgb, mix, mutedOn, parseHsl, type Rgb, readableOn, stateOn, toHex } from "./color";

/**
 * Les SEULES variables qu'une marque d'école peut poser.
 *
 * Elles étaient neuf, puis deux : la police, la palette complète et le rayon ont
 * été retirés un par un, chacun parce qu'il permettait d'abîmer une page sans en
 * améliorer aucune. Le critère de ces retraits est le seul qui vaille pour un
 * ajout, et il vaut dans les deux sens : est-ce que ça rend une page MEILLEURE, ou
 * seulement différente ?
 *
 * L'aplat de navigation le rend meilleure, et on peut le prouver : tant qu'il se
 * dérivait de la couleur d'ACTION sous un texte blanc écrit en dur, une école à
 * couleur claire devenait illisible — un jaune à 2,09:1 pour un seuil AA de 4,5:1.
 * Les six écoles semées passaient (ACE, la plus juste, à 4,97:1) : le défaut était
 * latent, pas visible. Séparer les deux rôles corrige un défaut, il n'ajoute pas
 * un réglage.
 *
 * `nav-foreground` est dans la liste mais ne se saisit JAMAIS : il se calcule par
 * luminance du fond obtenu. Une école ne peut donc pas se rendre illisible.
 */
export const BRANDABLE_VARS = [
	"--mg-action-primary",
	"--mg-on-primary",
	"--mg-nav-bg",
	"--mg-nav-fg",
	"--mg-nav-fg-muted",
	"--mg-nav-item-hover",
	"--mg-nav-item-selected",
] as const;

/**
 * Les deux encres du référentiel, seules candidates pour un texte sur aplat.
 *
 * ⚠️ Elles RECOPIENT `--mg-text-heading` et `--mg-text-inverse` de
 * `tokens.css` : le calcul de contraste a besoin de valeurs RGB, et une feuille de
 * style ne s'importe pas. `tests/brand.test.ts` compare les deux fichiers et échoue
 * s'ils divergent — deux littéraux qui doivent rester égaux par convention finissent
 * toujours par ne plus l'être.
 */
export const INK: Rgb = [0x22, 0x1f, 0x1d];
export const PAPER: Rgb = [0xff, 0xff, 0xff];
/** Le fond de nav Mentor Goal, vers lequel une couleur d'ACTION est assombrie. */
const DEPTH: Rgb = [0x10, 0x13, 0x2c];

/**
 * Les cinq valeurs de la navigation, tirées d'UNE couleur.
 *
 * C'est ce qui distingue ce champ de la palette retirée : l'école en remplit une,
 * le reste s'accorde. Ouvrir les cinq laisserait une école désaccorder son propre
 * aplat — un survol plus clair que le sélectionné, un texte atténué invisible.
 *
 * ⚠️ `assombrir` dit d'où vient la couleur, et il change le résultat :
 *
 *   - une couleur d'APLAT, choisie par l'école dans le sélecteur, se prend TELLE
 *     QUELLE. On l'assombrissait aussi, et c'était un mensonge : le carré cliqué
 *     n'était pas celui qu'on obtenait, et l'aplat du référentiel (#263171) rendait
 *     #222c65. Une école qui pose son bleu de marque doit voir son bleu de marque.
 *
 *   - une couleur d'ACTION, reprise faute d'aplat, se dépose vers `DEPTH`. Elle est
 *     faite pour ressortir sur du blanc ; en pleine hauteur elle crie.
 */
function navVars(base: Rgb, assombrir: boolean): Record<string, string> {
	const background = assombrir ? mix(base, DEPTH, 0.82) : base;
	const foreground = readableOn(background, INK, PAPER);

	// ⚠️ Le survol et la sélection ne prennent PAS un ratio fixe.
	//
	// Ils en prenaient un, vers le texte, et c'était la même erreur que le blanc en
	// dur rejouée deux crans plus loin : rapprocher un fond de son texte lui enlève
	// du contraste. Balayé sur les 20 196 couleurs de l'espace HSL, 34 % passaient
	// sous le seuil AA, le pire à 2,62:1 sur un beige. Ces deux états portent le
	// même libellé 13px que le reste de la barre : ils ont le même seuil.
	//
	// `stateOn` tient les deux promesses à la fois — se voir, et rester lisible —
	// et c'est nécessaire : s'éloigner simplement du texte réglait le contraste mais
	// rendait le survol INVISIBLE sur un aplat déjà noir ou déjà blanc, où il n'y a
	// plus nulle part où s'éloigner.
	return {
		"--mg-nav-bg": toHex(background),
		"--mg-nav-fg": toHex(foreground),
		// L'atténué s'éloigne du texte vers le fond aussi loin que le seuil le permet.
		// Un ratio fixe donnait 4,1:1 sur un aplat sombre et 2,77:1 sur un clair.
		"--mg-nav-fg-muted": toHex(mutedOn(foreground, background)),
		//
		// Les deux nombres sont des SÉPARATIONS visées, pas des pas : le survol se
		// détache juste assez pour se voir, la sélection franchement. Ils étaient des
		// pas, et les deux états rendaient alors la même couleur — #3f38b0 contre
		// #3e37ae sur l'indigo par défaut, mesuré sur une capture. Une différence
		// d'un cran par canal n'est pas une différence.
		"--mg-nav-item-hover": toHex(stateOn(background, foreground, 1.12)),
		"--mg-nav-item-selected": toHex(stateOn(background, foreground, 1.45)),
	};
}

/**
 * Traduit une marque en variables CSS, en ignorant tout ce qui est mal formé.
 *
 * Ignorer plutôt que rejeter : une couleur cassée dans la configuration d'une
 * école doit dégrader vers le thème Mentor Goal, jamais faire écran blanc chez le
 * recruteur qui n'y peut rien.
 */
export function brandToCssVars(brand: Brand): CSSProperties {
	const vars: Record<string, string> = {};

	const accent = brand.accent ? parseHsl(brand.accent) : null;
	if (accent) {
		vars["--mg-action-primary"] = `hsl(${brand.accent})`;
	}
	if (brand.accentContrast && parseHsl(brand.accentContrast)) {
		vars["--mg-on-primary"] = `hsl(${brand.accentContrast})`;
	}

	// L'aplat prend sa propre couleur quand l'école en a choisi une, sinon celle de
	// l'action. Une couleur d'action est faite pour ressortir sur du blanc ; un
	// aplat pleine hauteur est fait pour se tenir en arrière-plan. Ce sont deux
	// rôles, et une école à l'orange vif a un bon bouton et une barre criarde.
	const choisi = brand.navBase ? parseHsl(brand.navBase) : null;
	const navBase = choisi ?? accent;
	if (navBase) {
		Object.assign(vars, navVars(hslToRgb(...navBase), choisi === null));
	}

	return vars as CSSProperties;
}

/** Le nom affiché : le campus quand il existe, sinon l'école. */
export function brandDisplayName(brand: Brand): string {
	return brand.campusName ? `${brand.schoolName} ${brand.campusName}` : brand.schoolName;
}
